import React, { useState, useEffect, useCallback } from 'react';
import { Youtube, Instagram, Mail, User } from 'lucide-react';
import { EPCard } from '../components/EPCard';
import { FeaturedTrack } from '../components/FeaturedTrack';
import { GlobalAudioPlayer } from '../components/GlobalAudioPlayer';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Home = () => {
  const [eps, setEps] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [epsResponse, settingsResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/eps`),
        axios.get(`${BACKEND_URL}/api/settings`)
      ]);
      
      setEps(epsResponse.data);
      setSettings(settingsResponse.data);
    } finally {
      setLoading(false);
    }
  }, []);

  const recordPageView = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/analytics/page-view`);
    } catch (error) {
      // Analytics failure shouldn't block page load
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Record page view
    recordPageView();

    // Auto-refresh EP list so scheduled EPs/tracks appear without a manual reload
    const interval = setInterval(fetchData, 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchData, recordPageView]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-purple-400 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-24">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-lg border-b border-purple-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={settings?.logo || "https://customer-assets.emergentagent.com/job_music-portfolio-55/artifacts/nen4q40t_PN0v4.png"} 
              alt="PN0VΛ"
              className="h-12 w-auto"
            />
          </div>
          
          <nav className="flex items-center gap-6">
            <a href="#music" className="text-purple-300 hover:text-white transition-colors">Music</a>
            <a href="#bio" className="text-purple-300 hover:text-white transition-colors">Bio</a>
            <a href="#contact" className="text-purple-300 hover:text-white transition-colors">Contact</a>
            <Button 
              asChild
              variant="ghost"
              size="icon"
              className="text-purple-300 hover:text-white hover:bg-purple-500/20"
            >
              <a href="/admin">
                <User className="h-5 w-5" />
              </a>
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 via-black to-black"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
        }}></div>
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <img 
            src={settings?.logo || "https://customer-assets.emergentagent.com/job_music-portfolio-55/artifacts/nen4q40t_PN0v4.png"} 
            alt="PN0VΛ"
            className="w-full max-w-2xl mx-auto mb-8 drop-shadow-2xl"
            style={{ filter: 'drop-shadow(0 0 40px rgba(139, 92, 246, 0.5))' }}
          />
          <p className="text-purple-200 text-xl md:text-2xl mb-8 leading-relaxed max-w-2xl mx-auto">
            Polished, vocal-driven pop with cinematic elements and modern production
          </p>
          <div className="flex gap-4 justify-center">
            <Button 
              asChild
              className="bg-purple-600 hover:bg-purple-500 text-white px-8 py-6 text-lg rounded-full"
            >
              <a href="#music">Explore Music</a>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-purple-500 text-purple-300 hover:bg-purple-500/20 px-8 py-6 text-lg rounded-full"
            >
              <a href={settings?.social_media?.youtube || "https://www.youtube.com/@Pnov4"} target="_blank" rel="noopener noreferrer">
                <Youtube className="mr-2 h-5 w-5" />
                YouTube
              </a>
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-purple-500 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-purple-500 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Featured Track Section */}
      <FeaturedTrack />

      {/* Music Section */}
      <section id="music" className="py-20 px-4 bg-gradient-to-b from-black via-purple-950/10 to-black">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Discography
          </h2>
          <p className="text-purple-300 text-center mb-12 text-lg">
            Explore the complete catalog
          </p>
          
          {eps.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {eps.map(ep => (
                <EPCard key={ep.id} ep={ep} />
              ))}
            </div>
          ) : (
            <div className="text-center text-purple-400">
              <p>No releases yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Bio Section with Photo */}
      <section id="bio" className="py-20 px-4 bg-gradient-to-b from-black via-purple-950/10 to-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            About
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Photo - Left on desktop, Top on mobile */}
            <div className="order-1">
              <div className="relative aspect-square max-w-md mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-2xl blur-2xl"></div>
                <img 
                  src={settings?.profile_image_url || "https://customer-assets.emergentagent.com/job_music-portfolio-55/artifacts/cogtg0tu_My%20face.PNG"}
                  alt="PN0VΛ"
                  className="relative z-10 w-full h-full object-cover rounded-2xl border-2 border-purple-500/30 shadow-2xl shadow-purple-500/20"
                />
              </div>
            </div>

            {/* Bio - Right on desktop, Bottom on mobile */}
            <div className="order-2">
              <div className="bg-gradient-to-br from-purple-900/20 to-black border border-purple-500/30 rounded-2xl p-8">
                <div className="prose prose-invert prose-purple max-w-none">
                  {settings?.bio ? (
                    settings.bio.split('\n\n').map((paragraph, index) => (
                      // Index as key is safe here - static text paragraphs that don't reorder
                      <p key={`bio-${index}`} className="text-purple-100 text-lg leading-relaxed mb-6 last:mb-0">
                        {paragraph}
                      </p>
                    ))
                  ) : (
                    <p className="text-purple-100 text-lg leading-relaxed">
                      PN0VΛ is the production project of Peter Wilks, focused on crafting polished, vocal-driven pop with a strong emphasis on structure, emotion, and modern production.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-b from-black via-purple-950/10 to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Get In Touch
          </h2>
          
          <div className="flex flex-wrap gap-6 justify-center">
            <Button 
              asChild
              size="lg"
              className="bg-purple-600 hover:bg-purple-500 text-white px-8 py-6 text-lg rounded-full"
            >
              <a href={`mailto:${settings?.email || 'contact.pn0va.music@gmail.com'}`}>
                <Mail className="mr-2 h-5 w-5" />
                {settings?.email || 'contact.pn0va.music@gmail.com'}
              </a>
            </Button>
            
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-purple-500 text-purple-300 hover:bg-purple-500/20 px-8 py-6 text-lg rounded-full"
            >
              <a href={settings?.social_media?.youtube || "https://www.youtube.com/@Pnov4"} target="_blank" rel="noopener noreferrer">
                <Youtube className="mr-2 h-5 w-5" />
                YouTube
              </a>
            </Button>
            
            <Button 
              asChild
              size="lg"
              variant="outline"
              className="border-purple-500 text-purple-300 hover:bg-purple-500/20 px-8 py-6 text-lg rounded-full"
            >
              <a href={settings?.social_media?.instagram || "https://instagram.com/p.n0v4"} target="_blank" rel="noopener noreferrer">
                <Instagram className="mr-2 h-5 w-5" />
                Instagram
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-purple-500/20 bg-black">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-purple-400">
            © {new Date().getFullYear()} PN0VΛ. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Global Audio Player */}
      <GlobalAudioPlayer />
    </div>
  );
};